import React, { useState, useRef, useEffect } from "react";
import { Menu, MenuItem, ListItemText, ListItemIcon } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

interface BaseNestedMenuItemProps {
  label: string;
  children?: React.ReactNode;
  onClick?: () => void;
}

interface NestedMenuItemProps extends BaseNestedMenuItemProps {
  parentMenuOpen: boolean;
  activeItem: string | null;
  setActiveItem: (item: string | null) => void;
}

interface NestedMenuContainerProps {
  children: React.ReactNode;
  parentMenuOpen: boolean;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({
  label,
  children,
  onClick,
  parentMenuOpen,
  activeItem,
  setActiveItem,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuItemRef = useRef<HTMLLIElement>(null);
  const isSubMenuOpen =
    Boolean(anchorEl) && parentMenuOpen && activeItem === label;
  const [subMenuPosition, setSubMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!parentMenuOpen) {
      setAnchorEl(null);
      setActiveItem(null);
    }
  }, [parentMenuOpen, setActiveItem]);

  const handleMouseEnter = (_event: React.MouseEvent<HTMLLIElement>) => {
    if (children) {
      const menuItem = menuItemRef.current;
      if (menuItem) {
        const { right, top } = menuItem.getBoundingClientRect();
        setSubMenuPosition({
          left: right,
          top: top,
        });
        setAnchorEl(menuItem);
        setActiveItem(label);
      }
    }
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isHoveringSubmenu = relatedTarget?.closest(".MuiMenu-paper");

    if (!isHoveringSubmenu) {
      setAnchorEl(null);
      setActiveItem(null);
    }
  };

  const handleClick = () => {
    if (!children && onClick) {
      onClick();
    }
  };

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <ListItemText primary={label} />
        {children && (
          <ListItemIcon sx={{ marginLeft: 1 }}>
            <KeyboardArrowRightIcon />
          </ListItemIcon>
        )}
      </MenuItem>

      {children && (
        <Menu
          open={isSubMenuOpen}
          onClose={() => {
            setAnchorEl(null);
            setActiveItem(null);
          }}
          anchorReference="anchorPosition"
          anchorPosition={subMenuPosition}
          style={{ pointerEvents: "none" }}
          PaperProps={{
            style: { pointerEvents: "auto" },
            onMouseLeave: () => {
              setAnchorEl(null);
              setActiveItem(null);
            },
          }}
          sx={{
            "& .MuiMenu-paper": {
              minWidth: "150px",
            },
          }}
        >
          {children}
        </Menu>
      )}
    </>
  );
};

const NestedMenuContainer: React.FC<NestedMenuContainerProps> = ({
  children,
  parentMenuOpen,
}) => {
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement<BaseNestedMenuItemProps>(child)) {
      return React.cloneElement(child, {
        ...child.props,
        activeItem,
        setActiveItem,
        parentMenuOpen,
      } as NestedMenuItemProps);
    }
    return child;
  });

  return <>{childrenWithProps}</>;
};

// Create a wrapper component that doesn't require the internal props
const NestedMenuItemWrapper: React.FC<BaseNestedMenuItemProps> = (props) => {
  return <NestedMenuItem {...(props as NestedMenuItemProps)} />;
};

// Export both the wrapper and container
const NestedMenuComponents = {
  Item: NestedMenuItemWrapper,
  Container: NestedMenuContainer,
};

export default NestedMenuComponents;
